import React, { useState, useEffect, useRef } from "react";
import { useRestaurantCart, checkoutClient, getSiteConfig } from "../happyspace-restaurant-sdk";
import { BRAND_NAME } from '../config/brand';
import { Button, Input, Label } from "../components/ui";
import { Loader2, ShoppingBag, ArrowLeft, CreditCard, Lock, Plus, Minus, Trash2, MapPin, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Textarea = ({ className, ...props }: any) => (
  <textarea
    className={`flex min-h-[72px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:opacity-50 ${className}`}
    {...props}
  />
);

// Load Stripe.js from CDN
const loadStripeCDN = (publishableKey: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    if ((window as any).Stripe) {
      resolve((window as any).Stripe(publishableKey));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => {
      if ((window as any).Stripe) {
        resolve((window as any).Stripe(publishableKey));
      } else {
        reject(new Error("Stripe failed to load"));
      }
    };
    script.onerror = () => reject(new Error("Failed to load Stripe.js"));
    document.head.appendChild(script);
  });
};

function EditableCartItem({ item, onUpdateQuantity, onRemove }: { item: any; onUpdateQuantity: (key: string, qty: number) => void; onRemove: (key: string) => void }) {
  const k = item.cartItemKey;
  const qty = item.quantity || 1;
  const price = ((item.basePrice || 0) * qty).toFixed(2);
  return (
    <div className="flex gap-3 py-3 border-b border-white/10 last:border-0">
      {item.image && <img src={item.image} alt={item.title} className="w-12 h-12 object-cover rounded-lg flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start">
          <div>
            <p className="font-medium text-sm truncate">{item.title}</p>
            {item.selectedModifiers?.length > 0 && (
              <p className="text-xs text-white/60 truncate">{item.selectedModifiers.map((m: any) => m.name || m.options?.map((o: any) => o.name).join(", ")).join(" • ")}</p>
            )}
          </div>
          <button type="button" onClick={() => onRemove(k)} className="p-1 text-white/50 hover:text-red-300" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-0.5 bg-white/10 rounded-md">
            <button type="button" onClick={() => onUpdateQuantity(k, qty - 1)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/20"><Minus className="w-3 h-3" /></button>
            <span className="w-5 text-center text-xs font-medium">{qty}</span>
            <button type="button" onClick={() => onUpdateQuantity(k, qty + 1)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/20"><Plus className="w-3 h-3" /></button>
          </div>
          <span className="font-semibold text-sm">${price}</span>
        </div>
      </div>
    </div>
  );
}

// Main Checkout Page Component with Apple Pay / Google Pay Support
export default function CheckoutPage() {
  const navigate = useNavigate();
  const { 
    items, 
    subtotal, 
    tax, 
    total, 
    itemCount, 
    orderType, 
    setOrderType, 
    clearCart,
    removeItem,
    updateQuantity
  } = useRestaurantCart();
  
  const [stripe, setStripe] = useState<any>(null);
  const [elements, setElements] = useState<any>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canMakeWalletPayment, setCanMakeWalletPayment] = useState(false);
  const [walletPaymentType, setWalletPaymentType] = useState<string | null>(null);
  
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const walletButtonRef = useRef<HTMLDivElement>(null);

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "wallet">("card");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    address: "",
    city: "",
    state: "TX",
    zip: "",
  });

  // Load Stripe and setup payment methods
  useEffect(() => {
    const initStripe = async () => {
      try {
        const { apiBaseUrl, businessId } = getSiteConfig();
        const response = await fetch(`${apiBaseUrl}/public/payments/${businessId}/config`);
        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to load payment configuration");
        }

        if (!data.paymentEnabled) {
          setConfigError("Online payments are not currently available. Please contact the restaurant.");
          setLoadingConfig(false);
          return;
        }

        if (!data.stripePublishableKey) {
          throw new Error("Payment system is not configured for this business");
        }

        const stripeInstance = await loadStripeCDN(data.stripePublishableKey);
        setStripe(stripeInstance);
        
        const elementsInstance = stripeInstance.elements();
        setElements(elementsInstance);
        
        setLoadingConfig(false);
      } catch (err: any) {
        console.error("[Checkout] Failed to initialize Stripe:", err);
        setConfigError(err.message || "Could not load payment system. Please try again.");
        setLoadingConfig(false);
      }
    };

    initStripe();
  }, []);

  // Setup Payment Request (Apple Pay / Google Pay) when Stripe and total are ready
  useEffect(() => {
    if (!stripe || !elements || total <= 0) return;

    const orderLabel = `${BRAND_NAME} Order`;
    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: orderLabel,
        amount: Math.round(total * 100), // Convert to cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    // Check if Apple Pay or Google Pay is available
    pr.canMakePayment().then((result: any) => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakeWalletPayment(true);
        // Determine which wallet is available
        if (result.applePay) {
          setWalletPaymentType("applePay");
        } else if (result.googlePay) {
          setWalletPaymentType("googlePay");
        } else {
          setWalletPaymentType("wallet");
        }
        console.log("[Checkout] Digital wallet available:", result);
      } else {
        console.log("[Checkout] No digital wallet available");
      }
    });

    setPaymentRequest(pr);
  }, [stripe, elements, total]);

  // Mount Payment Request Button when available
  useEffect(() => {
    if (!elements || !paymentRequest || !canMakeWalletPayment || !walletButtonRef.current) return;

    const prButton = elements.create("paymentRequestButton", {
      paymentRequest,
      style: {
        paymentRequestButton: {
          type: "default",
          theme: "dark",
          height: "56px",
        },
      },
    });

    prButton.mount(walletButtonRef.current);

    return () => {
      prButton.unmount();
    };
  }, [elements, paymentRequest, canMakeWalletPayment]);

  // Handle Payment Request (Apple Pay / Google Pay) payment
  useEffect(() => {
    if (!paymentRequest) return;

    paymentRequest.on("paymentmethod", async (ev: any) => {
      setLoading(true);
      setError(null);

      try {
        console.log("[Checkout] Wallet payment initiated:", ev.paymentMethod.id);

        // Get customer info from wallet
        const walletName = ev.payerName || formData.name || "Customer";
        const walletEmail = ev.payerEmail || formData.email;
        const walletPhone = ev.payerPhone || formData.phone;

        if (!walletEmail) {
          ev.complete("fail");
          setError("Email is required. Please fill in your email address.");
          setLoading(false);
          return;
        }

        const walletDeliveryAddress = orderType === "delivery" && formData.address && formData.zip
          ? [formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")
          : null;
        if (orderType === "delivery" && !walletDeliveryAddress) {
          ev.complete("fail");
          setError("Delivery address is required. Please enter your address.");
          setLoading(false);
          return;
        }

        // Create payment intent on backend
        const intentResponse = await checkoutClient.createPaymentIntent({
          items,
          customer: {
            name: walletName,
            email: walletEmail,
            phone: walletPhone,
          },
          orderType,
          deliveryAddress: walletDeliveryAddress,
        });

        if (!intentResponse.clientSecret) {
          ev.complete("fail");
          throw new Error("Could not initialize payment. Please try again.");
        }

        // Confirm the payment with the wallet payment method
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
          intentResponse.clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (stripeError) {
          ev.complete("fail");
          throw new Error(stripeError.message || "Payment failed. Please try again.");
        }

        if (paymentIntent.status === "requires_action") {
          // Handle 3D Secure if needed
          ev.complete("success");
          const { error: confirmError } = await stripe.confirmCardPayment(intentResponse.clientSecret);
          if (confirmError) {
            throw new Error(confirmError.message || "Payment confirmation failed.");
          }
        } else {
          ev.complete("success");
        }

        if (paymentIntent?.status !== "succeeded") {
          throw new Error("Payment was not completed. Please try again.");
        }

        console.log("[Checkout] Wallet payment succeeded! Submitting order...");

        // Submit order to backend
        const orderResponse = await checkoutClient.submitOrder({
          items,
          customer: {
            name: walletName,
            email: walletEmail,
            phone: walletPhone,
          },
          paymentIntentId: intentResponse.paymentIntentId,
          paymentMethod: "stripe",
          orderType,
          deliveryAddress: walletDeliveryAddress,
          notes: formData.notes || "",
        });

        if (!orderResponse.success || !orderResponse.order) {
          throw new Error("Order could not be created. Please contact support.");
        }

        console.log("[Checkout] Order created successfully:", orderResponse.order.orderId);

        clearCart();

        const orderInfo = {
          orderId: orderResponse.order.orderId,
          email: walletEmail,
          orderNumber: orderResponse.order.orderNumber,
          status: orderResponse.order.status,
          total: orderResponse.order.total,
          orderType: orderType,
        };
        sessionStorage.setItem("lastOrder", JSON.stringify(orderInfo));

        navigate(`/order-status/${orderResponse.order.orderId}?email=${encodeURIComponent(walletEmail)}`);
      } catch (err: any) {
        console.error("[Checkout] Wallet payment error:", err);
        setError(err.message || "Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    });
  }, [paymentRequest, stripe, items, orderType, formData, navigate, clearCart]);

  useEffect(() => {
    if (paymentRequest && total > 0) {
      paymentRequest.update({ total: { label: `${BRAND_NAME} Order`, amount: Math.round(total * 100) } });
    }
  }, [paymentRequest, total]);

  // Mount card element when Stripe is ready
  useEffect(() => {
    if (stripe && elements && cardContainerRef.current && !cardElement) {
      const card = elements.create("card", {
        style: {
          base: {
            fontSize: "16px",
            color: "#451A03",
            fontFamily: "Inter, system-ui, sans-serif",
            "::placeholder": { color: "#9CA3AF" },
          },
          invalid: { color: "#EF4444", iconColor: "#EF4444" },
        },
      });

      card.mount(cardContainerRef.current);
      card.on("change", (event: any) => {
        setCardComplete(event.complete);
        if (event.error) {
          setError(event.error.message);
        } else {
          setError(null);
        }
      });

      setCardElement(card);
    }

    return () => {
      if (cardElement) {
        cardElement.unmount();
      }
    };
  }, [stripe, elements, cardElement]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateQuantity = (cartItemKey: string, newQty: number) => {
    updateQuantity(cartItemKey, newQty);
  };

  const handleRemoveItem = (cartItemKey: string) => {
    removeItem(cartItemKey);
  };

  // Handle card payment submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !cardElement) {
      setError("Payment system not loaded. Please refresh the page.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log("[Checkout] Creating payment intent...");
      const deliveryAddressFull = orderType === "delivery" && formData.address && formData.zip
        ? [formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")
        : null;

      const intentResponse = await checkoutClient.createPaymentIntent({
        items,
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        orderType,
        deliveryAddress: deliveryAddressFull,
      });

      if (!intentResponse.clientSecret) {
        throw new Error("Could not initialize payment. Please try again.");
      }

      console.log("[Checkout] Payment intent created, confirming payment...");

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        intentResponse.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || "Payment failed. Please try again.");
      }

      if (paymentIntent?.status !== "succeeded") {
        throw new Error("Payment was not completed. Please try again.");
      }

      console.log("[Checkout] Payment succeeded! Submitting order...");

      const orderResponse = await checkoutClient.submitOrder({
        items,
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        paymentIntentId: intentResponse.paymentIntentId,
        paymentMethod: "stripe",
        orderType,
        deliveryAddress: deliveryAddressFull,
        notes: formData.notes || "",
      });

      if (!orderResponse.success || !orderResponse.order) {
        throw new Error("Order could not be created. Please contact support.");
      }

      console.log("[Checkout] Order created successfully:", orderResponse.order.orderId);

      clearCart();

      const orderInfo = {
        orderId: orderResponse.order.orderId,
        email: formData.email,
        orderNumber: orderResponse.order.orderNumber,
        status: orderResponse.order.status,
        total: orderResponse.order.total,
        orderType: orderType,
      };
      sessionStorage.setItem("lastOrder", JSON.stringify(orderInfo));

      navigate(`/order-status/${orderResponse.order.orderId}?email=${encodeURIComponent(formData.email)}`);
    } catch (err: any) {
      console.error("[Checkout] Error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deliveryAddressFull = orderType === "delivery"
    ? [formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")
    : null;
  const isFormValid = formData.name && formData.email && formData.phone &&
    (orderType === "pickup" || (formData.address && formData.zip));

  if (itemCount === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <ShoppingBag className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 text-sm mb-6">Add items from the menu to get started.</p>
        <Button onClick={() => navigate("/menu")} className="rounded-xl">Browse menu</Button>
      </div>
    );
  }

  if (loadingConfig) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-amber-600" />
        <p className="text-gray-600 font-medium">Loading checkout…</p>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="max-w-md p-6 bg-white rounded-2xl border border-red-200 shadow-sm text-center">
          <p className="text-red-600 font-medium mb-4">{configError}</p>
          <Button onClick={() => window.location.reload()} className="rounded-xl">Try again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/menu")} className="flex items-center gap-2 text-gray-600 hover:text-amber-600 mb-6 text-sm font-medium transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to menu
        </button>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Left: Form */}
            <div className="lg:col-span-7 space-y-6">
              {/* 1. Order type */}
              <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">How would you like your order?</h2>
                <div className="grid grid-cols-2 gap-3">
                  <label className={`flex items-center gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all hover:border-amber-200 ${orderType === "pickup" ? "border-amber-500 bg-amber-50" : "border-gray-200 bg-gray-50"}`}>
                    <input type="radio" name="orderType" value="pickup" checked={orderType === "pickup"} onChange={() => setOrderType("pickup")} className="sr-only" />
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><MapPin className="w-5 h-5 text-amber-600" /></div>
                    <div>
                      <span className="font-semibold block">Pickup</span>
                      <span className="text-xs text-gray-500">Ready in 15–20 min</span>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all hover:border-amber-200 ${orderType === "delivery" ? "border-amber-500 bg-amber-50" : "border-gray-200 bg-gray-50"}`}>
                    <input type="radio" name="orderType" value="delivery" checked={orderType === "delivery"} onChange={() => setOrderType("delivery")} className="sr-only" />
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><Truck className="w-5 h-5 text-amber-600" /></div>
                    <div>
                      <span className="font-semibold block">Delivery</span>
                      <span className="text-xs text-gray-500">Fee added at checkout • 45–60 min</span>
                    </div>
                  </label>
                </div>
              </section>

              {/* 2. Your info */}
              <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Your details</h2>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-700">Name *</Label>
                      <Input id="name" name="name" required value={formData.name} onChange={handleInputChange} placeholder="John Doe" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-700">Phone *</Label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleInputChange} placeholder="(555) 123-4567" className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700">Email *</Label>
                    <Input id="email" name="email" type="email" required value={formData.email} onChange={handleInputChange} placeholder="john@example.com" className="mt-1" />
                  </div>

                  {orderType === "delivery" && (
                    <div className="pt-4 border-t border-gray-100 space-y-4">
                      <p className="text-sm font-medium text-gray-700">Delivery address</p>
                      <Input name="address" required value={formData.address} onChange={handleInputChange} placeholder="Street address" />
                      <div className="grid grid-cols-3 gap-3">
                        <Input name="city" value={formData.city} onChange={handleInputChange} placeholder="City" />
                        <Input name="state" value={formData.state} onChange={handleInputChange} placeholder="State" />
                        <Input name="zip" required value={formData.zip} onChange={handleInputChange} placeholder="ZIP" />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="notes" className="text-gray-700">Special instructions</Label>
                    <Textarea id="notes" name="notes" placeholder="Allergies, extra sauce, etc." value={formData.notes} onChange={handleInputChange} className="mt-1" />
                  </div>
                </div>
              </section>

              {/* 3. Payment */}
              <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" /> Payment
                </h2>
                <div className="space-y-4">
                  {canMakeWalletPayment && (
                    <>
                      <div ref={walletButtonRef} className="w-full min-h-[48px]" />
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-sm text-gray-500">or pay with card</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    </>
                  )}
                  <div ref={cardContainerRef} className="p-4 rounded-xl border border-gray-200 bg-gray-50 min-h-[48px]" />
                  <p className="flex items-center gap-2 text-xs text-gray-500">
                    <Lock className="w-3.5 h-3.5" /> Secured with SSL encryption
                  </p>
                </div>
              </section>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full py-6 text-lg font-semibold rounded-xl shadow-lg disabled:opacity-50"
                disabled={loading || !stripe || !cardComplete || !isFormValid}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" /> Processing…
                  </span>
                ) : (
                  `Place order — $${total.toFixed(2)}`
                )}
              </Button>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              )}
            </div>

            {/* Right: Order summary */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-8 bg-[#1c1917] text-white rounded-2xl overflow-hidden shadow-lg">
                <div className="p-5 flex justify-between items-center border-b border-white/10">
                  <h3 className="font-semibold">Your order</h3>
                  <button type="button" onClick={() => navigate("/menu")} className="text-sm text-amber-200/80 hover:text-amber-200">Add more</button>
                </div>
                <div className="p-5 max-h-[50vh] overflow-y-auto">
                  {items.map((item: any) => (
                    <EditableCartItem key={item.cartItemKey || item._id} item={item} onUpdateQuantity={handleUpdateQuantity} onRemove={handleRemoveItem} />
                  ))}
                </div>
                <div className="p-5 border-t border-white/10 space-y-2">
                  <div className="flex justify-between text-sm text-white/70"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-sm text-white/70"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
                  {orderType === "delivery" && <p className="text-xs text-amber-200/80">Delivery fee added at checkout</p>}
                  <div className="flex justify-between text-lg font-bold pt-2"><span>Total</span><span className="text-amber-400">${total.toFixed(2)}</span></div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
